import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, getAdminUser } from '../utils/api';

const ReportFilters = ({
  onFilterChange,
  showBranchFilter = true,
  showDentistFilter = false,
  showDateFilter = true,
  showExportButtons = true,
  onExport,
  onPrint,
  loading = false
}) => {
  const { print_lang } = useLanguage();
  const user = getAdminUser();
  const isSuperAdmin = user?.is_super_admin || user?.admin_id === 1;

  const [branches, setBranches] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [filters, setFilters] = useState({
    branch_id: '',
    dentist_id: '',
    preset: 'month',
    from_date: '',
    to_date: ''
  });

  // Load branches for dropdown
  useEffect(() => {
    if (showBranchFilter) {
      const fetchBranches = async () => {
        try {
          const data = await apiGet('/api/admin/reports/branches');
          if (data.success) {
            setBranches(data.data);
          }
        } catch (err) {
          console.error('Failed to load branches');
        }
      };
      fetchBranches();
    }
  }, [showBranchFilter]);

  // Load dentists when branch changes
  useEffect(() => {
    if (showDentistFilter) {
      const fetchDentists = async () => {
        try {
          const url = filters.branch_id
            ? `/api/admin/reports/dentists?branch_id=${filters.branch_id}`
            : '/api/admin/reports/dentists';
          const data = await apiGet(url);
          if (data.success) {
            setDentists(data.data);
          }
        } catch (err) {
          console.error('Failed to load dentists');
        }
      };
      fetchDentists();
    }
  }, [showDentistFilter, filters.branch_id]);

  // Memoize onFilterChange to stabilize reference
  const stableOnFilterChange = useCallback(onFilterChange, [onFilterChange]);

  // Notify parent when filters change
  useEffect(() => {
    stableOnFilterChange(filters);
  }, [filters, stableOnFilterChange]);

  const handlePresetChange = (preset) => {
    setFilters(prev => ({
      ...prev,
      preset,
      from_date: '',
      to_date: ''
    }));
  };

  const handleCustomDateChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      preset: 'custom',
      [field]: value
    }));
  };

  const presets = [
    { id: 'today', label: print_lang('reports.today', 'Today') },
    { id: 'week', label: print_lang('reports.thisWeek', 'This Week') },
    { id: 'month', label: print_lang('reports.thisMonth', 'This Month') },
    { id: 'year', label: print_lang('reports.thisYear', 'This Year') }
  ];

  return (
    <div className="card report-filters" style={{ marginBottom: '1rem' }}>
      <div className="report-filters-row">
        {/* Branch Filter */}
        {showBranchFilter && (
          <div className="form-group report-filter-field" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('reports.branch', 'Branch')}</label>
            <select
              className="form-select"
              value={filters.branch_id}
              onChange={(e) => setFilters(prev => ({ ...prev, branch_id: e.target.value }))}
            >
              <option value="">{isSuperAdmin ? print_lang('reports.allBranches', 'All Branches') : print_lang('reports.allMyBranches', 'All My Branches')}</option>
              {branches.map(branch => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dentist Filter */}
        {showDentistFilter && (
          <div className="form-group report-filter-field" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('reports.dentist', 'Dentist')}</label>
            <select
              className="form-select"
              value={filters.dentist_id}
              onChange={(e) => setFilters(prev => ({ ...prev, dentist_id: e.target.value }))}
            >
              <option value="">{print_lang('reports.allDentists', 'All Dentists')}</option>
              {dentists.map(dentist => (
                <option key={dentist.dentist_id} value={dentist.dentist_id}>
                  {dentist.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date Presets */}
        {showDateFilter && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('reports.period', 'Period')}</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {presets.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetChange(preset.id)}
                  className={`btn btn-sm ${filters.preset === preset.id ? 'btn-primary' : 'btn-outline'}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom Date Range */}
        {showDateFilter && (
          <>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{print_lang('reports.fromDate', 'From')}</label>
              <input
                type="date"
                className="form-input"
                value={filters.from_date}
                onChange={(e) => handleCustomDateChange('from_date', e.target.value)}
                style={{ width: '150px' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{print_lang('reports.toDate', 'To')}</label>
              <input
                type="date"
                className="form-input"
                value={filters.to_date}
                onChange={(e) => handleCustomDateChange('to_date', e.target.value)}
                style={{ width: '150px' }}
              />
            </div>
          </>
        )}

        {/* Export Buttons */}
        {showExportButtons && (
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            {onPrint && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={onPrint}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                {print_lang('reports.print', 'Print')}
              </button>
            )}
            {onExport && (
              <button
                type="button"
                className="btn btn-success"
                onClick={onExport}
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {print_lang('reports.exportCsv', 'Export CSV')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportFilters;
